import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, MapPinned, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { StorePicker } from '../../components/StorePicker';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import type {
  Campaign,
  MerchandiserRoute,
  MerchandisingOptions,
  PaginatedResponse,
} from '../../types/domain';

const weekdays = [
  ['monday', 'Mon'],
  ['tuesday', 'Tue'],
  ['wednesday', 'Wed'],
  ['thursday', 'Thu'],
  ['friday', 'Fri'],
  ['saturday', 'Sat'],
  ['sunday', 'Sun'],
] as const;

interface StopDraft {
  location: string;
  planned_duration_minutes: number;
  instructions: string;
}

interface RouteDraft {
  name: string;
  merchandiser: string;
  campaign: string;
  schedule_type: 'recurring' | 'one_off';
  starts_on: string;
  ends_on: string;
  scheduled_on: string;
  weekdays: string[];
  planned_start_time: string;
  planned_end_time: string;
  instructions: string;
  brands: string[];
  stops: StopDraft[];
}

function initialRouteDraft(campaign: Campaign): RouteDraft {
  return {
    name: '',
    merchandiser: '',
    campaign: campaign.id,
    schedule_type: 'recurring',
    starts_on: campaign.starts_on,
    ends_on: campaign.ends_on,
    scheduled_on: '',
    weekdays: [],
    planned_start_time: '09:00',
    planned_end_time: '17:00',
    instructions: '',
    brands: [],
    stops: [{ location: '', planned_duration_minutes: 30, instructions: '' }],
  };
}

function scheduleSummary(route: MerchandiserRoute) {
  if (route.schedule_type === 'one_off') return route.scheduled_on ?? 'One-off';
  const days = route.weekdays.map((day) => day.slice(0, 3)).join(', ');
  return `${days} · ${route.starts_on} to ${route.ends_on}`;
}

export function MerchandisingRoutesPanel({ campaign }: { campaign: Campaign }) {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const workspaceId = campaign.owner_organization;
  const [draft, setDraft] = useState(() => initialRouteDraft(campaign));
  const [routeToCancel, setRouteToCancel] = useState<MerchandiserRoute | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');

  const options = useQuery({
    queryKey: ['merchandising-options', workspaceId, accessToken],
    enabled: Boolean(accessToken && workspaceId),
    queryFn: () => apiRequest<MerchandisingOptions>(
      `/merchandiser-routes/options/?workspace=${workspaceId}`,
      accessToken ? { accessToken } : {},
    ),
  });
  const routes = useQuery({
    queryKey: ['merchandiser-routes', workspaceId, campaign.id, accessToken],
    enabled: Boolean(accessToken && workspaceId),
    queryFn: () => apiRequest<PaginatedResponse<MerchandiserRoute>>(
      `/merchandiser-routes/?workspace=${workspaceId}&campaign=${campaign.id}&page_size=100&ordering=planned_start_time,name`,
      accessToken ? { accessToken } : {},
    ),
  });
  const createRoute = useMutation({
    mutationFn: () => apiRequest<MerchandiserRoute>('/merchandiser-routes/', {
      ...(accessToken ? { accessToken } : {}),
      method: 'POST',
      body: JSON.stringify({
        organization: workspaceId,
        ...draft,
        starts_on: draft.starts_on || null,
        ends_on: draft.ends_on || null,
        scheduled_on: draft.scheduled_on || null,
        stops: draft.stops.map((stop, index) => ({ ...stop, sequence: index + 1 })),
      }),
    }),
    onSuccess: async () => {
      setDraft(initialRouteDraft(campaign));
      await queryClient.invalidateQueries({ queryKey: ['merchandiser-routes'] });
    },
  });
  const cancelRoute = useMutation({
    mutationFn: () => apiRequest<MerchandiserRoute>(
      `/merchandiser-routes/${routeToCancel?.id}/cancel/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: 'POST',
        body: JSON.stringify({ reason: cancellationReason }),
      },
    ),
    onSuccess: async () => {
      setRouteToCancel(null);
      setCancellationReason('');
      await queryClient.invalidateQueries({ queryKey: ['merchandiser-routes'] });
    },
  });

  const locations = options.data?.locations.filter((item) => item.campaign === campaign.id) ?? [];
  const brands = options.data?.brands.filter((item) => item.client === campaign.client) ?? [];
  const routeRows = routes.data?.results ?? [];
  const updateDraft = (changes: Partial<RouteDraft>) => setDraft((current) => ({ ...current, ...changes }));
  const changeScheduleType = (scheduleType: RouteDraft['schedule_type']) => {
    updateDraft({
      schedule_type: scheduleType,
      starts_on: scheduleType === 'recurring' ? campaign.starts_on : '',
      ends_on: scheduleType === 'recurring' ? campaign.ends_on : '',
      scheduled_on: '',
      weekdays: scheduleType === 'recurring' ? draft.weekdays : [],
    });
  };
  const toggleWeekday = (day: string) => updateDraft({
    weekdays: draft.weekdays.includes(day)
      ? draft.weekdays.filter((selected) => selected !== day)
      : [...draft.weekdays, day],
  });
  const toggleBrand = (brand: string) => updateDraft({
    brands: draft.brands.includes(brand)
      ? draft.brands.filter((selected) => selected !== brand)
      : [...draft.brands, brand],
  });
  const updateStop = (index: number, changes: Partial<StopDraft>) => {
    updateDraft({
      stops: draft.stops.map((stop, stopIndex) => (
        stopIndex === index ? { ...stop, ...changes } : stop
      )),
    });
  };
  const closeCancellation = () => {
    setRouteToCancel(null);
    setCancellationReason('');
    cancelRoute.reset();
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    createRoute.mutate();
  };

  return (
    <>
      <section className="content-card management-panel merchandising-create-panel">
        <div className="card-heading">
          <div>
            <h2>Merchandising routes</h2>
            <p>Schedule recurring or one-off visits to stores attached to this campaign.</p>
          </div>
          <CalendarClock size={20} />
        </div>
        {options.isLoading ? (
          <div className="supervision-prerequisite" role="status">
            <InlineSpinner />
            <div><strong>Loading route options</strong><p>Preparing Merchandisers and campaign stores.</p></div>
          </div>
        ) : null}
        {!options.isLoading && !(options.data?.merchandisers.length && locations.length) ? (
          <div className="supervision-prerequisite" role="status">
            <MapPinned size={22} />
            <div><strong>Schedule prerequisites are missing</strong><p>Add an active Merchandiser and at least one campaign store before creating a route.</p></div>
          </div>
        ) : null}
        {!options.isLoading && options.data?.merchandisers.length && locations.length ? (
          <form className="management-form" onSubmit={submit}>
            <div className="form-grid merchandising-route-meta">
              <label>Route name<input maxLength={120} onChange={(event) => updateDraft({ name: event.target.value })} placeholder="Monday Lahore stores" required value={draft.name} /></label>
              <label>Merchandiser<select onChange={(event) => updateDraft({ merchandiser: event.target.value })} required value={draft.merchandiser}><option value="">Select Merchandiser</option>{options.data.merchandisers.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
              <label>Schedule type<select onChange={(event) => changeScheduleType(event.target.value as RouteDraft['schedule_type'])} value={draft.schedule_type}><option value="recurring">Recurring weekly</option><option value="one_off">One-off</option></select></label>
              <label>Planned start<input onChange={(event) => updateDraft({ planned_start_time: event.target.value })} required type="time" value={draft.planned_start_time} /></label>
              <label>Planned end<input onChange={(event) => updateDraft({ planned_end_time: event.target.value })} required type="time" value={draft.planned_end_time} /></label>
            </div>
            {draft.schedule_type === 'recurring' ? (
              <div className="form-grid merchandising-route-meta">
                <label>Starts on<input max={campaign.ends_on} min={campaign.starts_on} onChange={(event) => updateDraft({ starts_on: event.target.value })} required type="date" value={draft.starts_on} /></label>
                <label>Ends on<input max={campaign.ends_on} min={draft.starts_on || campaign.starts_on} onChange={(event) => updateDraft({ ends_on: event.target.value })} required type="date" value={draft.ends_on} /></label>
                <fieldset className="merchandising-weekdays"><legend>Repeats on</legend><div>{weekdays.map(([day, shortName]) => <label key={day}><input checked={draft.weekdays.includes(day)} onChange={() => toggleWeekday(day)} type="checkbox" /> {shortName}</label>)}</div></fieldset>
              </div>
            ) : (
              <div className="form-grid merchandising-route-meta"><label>Visit date<input max={campaign.ends_on} min={campaign.starts_on} onChange={(event) => updateDraft({ scheduled_on: event.target.value })} required type="date" value={draft.scheduled_on} /></label></div>
            )}
            <label className="merchandising-instructions">Route instructions<textarea maxLength={4000} onChange={(event) => updateDraft({ instructions: event.target.value })} placeholder="Optional store coverage, display, or stock instructions" rows={3} value={draft.instructions} /></label>
            <fieldset className="merchandising-brands"><legend>Brands covered <small>Optional</small></legend>{brands.length ? <div>{brands.map((brand) => <label key={brand.id}><input checked={draft.brands.includes(brand.id)} onChange={() => toggleBrand(brand.id)} type="checkbox" /> {brand.name}</label>)}</div> : <p>No active brands are available for this campaign client.</p>}</fieldset>
            <fieldset className="merchandising-stops"><legend>Ordered store stops</legend>{draft.stops.map((stop, index) => {
              const selectedLocations = new Set(draft.stops.filter((_, stopIndex) => stopIndex !== index).map((item) => item.location));
              return <div className="merchandising-stop-row" key={index}><label>Store<StorePicker ariaLabel={`Route store ${index + 1}`} onChange={(location) => updateStop(index, { location })} options={locations.filter((location) => !selectedLocations.has(location.id)).map((location) => ({ id: location.id, name: location.venue_name, city: location.venue_city }))} required value={stop.location} /></label><label>Minutes<input max={720} min={1} onChange={(event) => updateStop(index, { planned_duration_minutes: Number(event.target.value) })} required type="number" value={stop.planned_duration_minutes} /></label><label>Stop instructions<input maxLength={4000} onChange={(event) => updateStop(index, { instructions: event.target.value })} placeholder="Optional" value={stop.instructions} /></label><button aria-label={`Remove store stop ${index + 1}`} className="icon-button" disabled={draft.stops.length === 1} onClick={() => updateDraft({ stops: draft.stops.filter((_, stopIndex) => stopIndex !== index) })} type="button"><Trash2 size={17} /></button></div>;
            })}<button className="button secondary" disabled={draft.stops.length >= locations.length} onClick={() => updateDraft({ stops: [...draft.stops, { location: '', planned_duration_minutes: 30, instructions: '' }] })} type="button"><Plus size={16} /> Add store stop</button></fieldset>
            <div className="form-actions"><span /><button className="button primary" disabled={createRoute.isPending} type="submit">{createRoute.isPending ? <InlineSpinner /> : <CalendarClock size={16} />} Create route</button></div>
            {createRoute.isError ? <p className="form-error" role="alert">{getApiErrorMessage(createRoute.error)}</p> : null}
          </form>
        ) : null}
      </section>

      <div className="section-heading"><div><h2>Scheduled routes</h2><p>Cancelled routes remain visible so planning history stays auditable.</p></div><span>{routes.data?.count ?? 0}</span></div>
      <DataPanel loading={routes.isLoading} error={routes.isError} empty={routeRows.length === 0}>
        <section className="content-card data-card"><div className="data-table-wrap"><table className="data-table"><thead><tr><th>Route</th><th>Merchandiser</th><th>Schedule</th><th>Stops</th><th>Status</th><th /></tr></thead><tbody>{routeRows.map((route) => <tr key={route.id}><td><strong>{route.name}</strong><small>{route.reference} · {route.planned_start_time.slice(0, 5)}–{route.planned_end_time.slice(0, 5)}</small><small>{route.brands.map((brand) => brand.name).join(', ') || 'All client brands'}</small></td><td>{route.merchandiser_name}</td><td><small>{scheduleSummary(route)}</small></td><td>{route.stops.map((stop) => <small className="merchandising-stop-name" key={stop.id}>{stop.sequence}. {stop.venue_name}</small>)}</td><td><StatusPill value={route.status} /></td><td>{route.status === 'active' ? <button className="button danger" onClick={() => setRouteToCancel(route)} type="button">Cancel</button> : <small>{route.cancellation_reason}</small>}</td></tr>)}</tbody></table></div></section>
      </DataPanel>

      {routeToCancel ? <div className="confirmation-backdrop" role="presentation"><section aria-label="Cancel merchandising route" aria-modal="true" className="confirmation-dialog" role="dialog"><button aria-label="Close cancellation" className="icon-button confirmation-close" onClick={closeCancellation} type="button"><X size={19} /></button><p className="eyebrow">{routeToCancel.reference}</p><h2>Cancel route</h2><p>Past visit completions remain in the audit history. Future occurrences will no longer be available.</p><label>Cancellation reason<textarea maxLength={240} onChange={(event) => setCancellationReason(event.target.value)} required rows={3} value={cancellationReason} /></label>{cancelRoute.isError ? <p className="form-error">{getApiErrorMessage(cancelRoute.error)}</p> : null}<div className="confirmation-actions"><button className="button secondary" onClick={closeCancellation} type="button">Keep route</button><button className="button danger" disabled={cancelRoute.isPending || !cancellationReason.trim()} onClick={() => cancelRoute.mutate()} type="button">{cancelRoute.isPending ? 'Cancelling…' : 'Cancel route'}</button></div></section></div> : null}
    </>
  );
}
